import { Component, OnInit, OnDestroy, ViewChild, ElementRef, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
declare let OpusMediaRecorder: any;

const workerOptions = {
  OggOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/OggOpusEncoder.wasm',
  WebMOpusEncoderWasmPath: 'https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/WebMOpusEncoder.wasm'
};

// Tempo (em segundos) de gravação até o cursor vermelho do waveform chegar ao limite abaixo e parar de avançar.
// Puramente visual/representativo, não reflete análise real de frequência do áudio.
const RECORD_CURSOR_CAP_SECONDS = 10;
const RECORD_CURSOR_CAP_PERCENT = 85;

const WAVEFORM_BAR_COUNT = 48;

// Alturas (em %) puramente representativas (não é uma análise real da amplitude do áudio) - geradas
// aleatoriamente a cada gravação para não repetir sempre o mesmo desenho de onda.
function generateRandomWaveformHeights(count: number): number[] {
  const heights: number[] = [];
  for (let i = 0; i < count; i++) {
    heights.push(Math.round(12 + Math.random() * 85));
  }
  return heights;
}

type ChatViewerAudioViewState = 'idle' | 'recording' | 'paused' | 'review';

@Component({
  selector: 'chat-viewer-audio',
  templateUrl: './chat-viewer-audio.component.html',
  styleUrls: ['./chat-viewer-audio.component.scss']
})
export class ChatViewerAudioComponent implements OnInit, OnDestroy {

  @ViewChild('audioPlayer') audioPlayerRef: ElementRef<HTMLAudioElement>;
  @ViewChild('waveformWrapper') waveformWrapperRef!: ElementRef<HTMLDivElement>;

  record;
  url;
  safeAudioUrl: SafeUrl | null = null;
  error;
  blob: any;
  currentStream: MediaStream; // Adicionar propriedade para armazenar o stream

  viewState: ChatViewerAudioViewState = 'idle';
  isPlaying = false;

  recordElapsedSeconds = 0;
  recordingCursorPercent = 0;

  playbackCurrentSeconds = 0;
  playbackDurationSeconds = 0;

  waveformHeights: number[] = generateRandomWaveformHeights(WAVEFORM_BAR_COUNT);
  readonly waveformTrackGroups = [0, 1];

  isSeeking = false;

  private recordTimerInterval: any;
  private recordAccumulatedMs = 0;
  private recordSegmentStartMs = 0;

  constructor(
    private domSanitizer: DomSanitizer,
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
  ) { }

  ngOnInit() {
    window.MediaRecorder = OpusMediaRecorder;
  }

  ngOnDestroy() {
    this.clearRecordTimer();
    this.onSeekEnd();
  }

  get waveformCursorPercent(): number {
    return this.viewState === 'review' ? this.reviewCursorPercent : this.recordingCursorPercent;
  }

  // Revela a frequência (barras) apenas até onde o cursor já passou; o restante mostra a linha reta por baixo.
  get recordingRevealClipPath(): string {
    return `inset(0 ${100 - this.recordingCursorPercent}% 0 0)`;
  }

  // Quando o cursor atinge o limite, ele para e quem passa a "andar" é a frequência já revelada (scroll contínuo).
  get isRecordingFlowing(): boolean {
    return this.viewState !== 'review' && this.recordingCursorPercent >= RECORD_CURSOR_CAP_PERCENT;
  }

  get reviewCursorPercent(): number {
    if (!this.playbackDurationSeconds) {
      return 0;
    }
    return Math.min(100, (this.playbackCurrentSeconds / this.playbackDurationSeconds) * 100);
  }

  sanitize(url: string) {
    return this.domSanitizer.sanitize(SecurityContext.URL, url);
  }

  formatTime(totalSeconds: number): string {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  private padZero(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  // Log leve em sessionStorage (contexto do fluxo, sem payload/dados sensíveis) no lugar de console.*
  private logDebug(context: string, detail?: string) {
    try {
      sessionStorage.setItem(`chat-viewer-audio:${context}`, JSON.stringify({
        timestamp: new Date().toISOString(),
        detail: detail ?? null,
      }));
    } catch (storageError) {
      // sessionStorage indisponível (ex.: modo privado) - ignora silenciosamente
    }
  }

  initiateRecording() {
    const mediaConstraints = {
      video: false,
      audio: {},
    };

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.logDebug('initiateRecording:not-supported');
      this.warningOneDeviceNeed('NOT_SUPPORTED');
      return;
    }

    if (!(window as any).isSecureContext) {
      this.logDebug('initiateRecording:insecure-context');
      this.warningOneDeviceNeed('INSECURE_CONTEXT');
      return;
    }

    const permissionPromise = (navigator as any).permissions?.query({ name: 'microphone' }) ?? Promise.resolve(undefined);

    permissionPromise
      .then((status: any) => {
        if (status && status.state === 'denied') {
          this.logDebug('initiateRecording:permission-denied', status.state);
          throw new Error('denied');
        }
        return navigator.mediaDevices.getUserMedia(mediaConstraints);
      })
      .then(this.successCallback.bind(this))
      .catch((error) => {
        this.logDebug('initiateRecording:catch', error?.message || String(error));
        this.warningOneDeviceNeed(error)
      });
  }

  warningOneDeviceNeed(error) {
    Swal.fire({
      title: this.translate.instant('AVISO'),
      text: this.translate.instant('AVISO_CHAT_VIEWER_AUDIO'),
      icon: 'warning',
      showCancelButton: false,
      confirmButtonText: 'OK',
    }).then((result) => {
      this.viewState = 'idle';
    })
  }

  successCallback(stream) {
    this.record = null;
    this.url = null;
    this.safeAudioUrl = null;
    this.blob = null;
    this.currentStream = stream;

    var options = {
      mimeType: "audio/ogg",
    };

    try {
      this.record = new OpusMediaRecorder(stream, options, workerOptions);

      this.record.ondataavailable = (e) => {
        this.logDebug('successCallback:ondataavailable', `size=${e.data?.size}`);
        if (e.data.size > 0) {
          this.processRecording(e.data);
        }
      };

      this.record.onerror = (e) => {
        this.logDebug('successCallback:recording-error', e?.error?.message || String(e));
        this.errorCallback(e);
      };

      this.record.start();
      this.recordAccumulatedMs = 0;
      this.recordElapsedSeconds = 0;
      this.recordingCursorPercent = 0;
      this.waveformHeights = generateRandomWaveformHeights(WAVEFORM_BAR_COUNT);
      this.viewState = 'recording';
      this.startRecordTimer();
    } catch (error) {
      this.logDebug('successCallback:catch', error?.message || String(error));
      this.errorCallback(error);
    }
  }

  pauseRecording() {
    if (this.record && this.record.state === 'recording') {
      this.record.pause();
    }
    this.stopRecordTimer();
    this.viewState = 'paused';
  }

  resumeRecording() {
    if (this.record && this.record.state === 'paused') {
      this.record.resume();
    }
    this.startRecordTimer();
    this.viewState = 'recording';
  }

  concluirGravacao() {
    this.stopRecordTimer();
    this.playbackDurationSeconds = this.recordElapsedSeconds;
    this.playbackCurrentSeconds = 0;
    this.stopRecording();
    this.viewState = 'review';
  }

  excluirGravacao() {
    Swal.fire({
      title: this.translate.instant('CONFIRMAR_EXCLUSAO_AUDIO_GRAVADO'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('SIM'),
      cancelButtonText: this.translate.instant('CANCELAR'),
      position: 'top',
      customClass: {
        content: 'swal-only-text'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetToIdle();
      }
    });
  }

  togglePlayback() {
    const audioEl = this.audioPlayerRef?.nativeElement;
    if (!audioEl) {
      return;
    }

    if (this.isPlaying) {
      audioEl.pause();
    } else {
      audioEl.play();
    }
  }

  // Permite clicar/arrastar sobre o waveform (apenas na revisão) para pular a reprodução para aquele ponto,
  // semelhante ao scrubber de áudio do WhatsApp.
  onSeekStart(event: MouseEvent | TouchEvent) {
    if (this.viewState !== 'review' || !this.playbackDurationSeconds) {
      return;
    }
    event.preventDefault();
    this.isSeeking = true;
    this.seekToPointer(event);
    window.addEventListener('mousemove', this.onSeekMove);
    window.addEventListener('mouseup', this.onSeekEnd);
    window.addEventListener('touchmove', this.onSeekMove, { passive: false });
    window.addEventListener('touchend', this.onSeekEnd);
  }

  private onSeekMove = (event: MouseEvent | TouchEvent) => {
    if (!this.isSeeking) {
      return;
    }
    event.preventDefault();
    this.seekToPointer(event);
  };

  private onSeekEnd = () => {
    if (!this.isSeeking) {
      return;
    }
    this.isSeeking = false;
    window.removeEventListener('mousemove', this.onSeekMove);
    window.removeEventListener('mouseup', this.onSeekEnd);
    window.removeEventListener('touchmove', this.onSeekMove);
    window.removeEventListener('touchend', this.onSeekEnd);
  };

  private seekToPointer(event: MouseEvent | TouchEvent) {
    const wrapperEl = this.waveformWrapperRef?.nativeElement;
    const audioEl = this.audioPlayerRef?.nativeElement;
    if (!wrapperEl || !audioEl || !this.playbackDurationSeconds) {
      return;
    }

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX;
    if (clientX == null) {
      return;
    }

    const rect = wrapperEl.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const targetSeconds = fraction * this.playbackDurationSeconds;

    audioEl.currentTime = targetSeconds;
    this.playbackCurrentSeconds = targetSeconds;
  }

  onAudioPlay() {
    this.isPlaying = true;
  }

  onAudioPause() {
    this.isPlaying = false;
  }

  onAudioTimeUpdate() {
    const audioEl = this.audioPlayerRef?.nativeElement;
    if (!audioEl) {
      return;
    }
    this.playbackCurrentSeconds = audioEl.currentTime;
  }

  onAudioEnded() {
    const audioEl = this.audioPlayerRef?.nativeElement;
    this.isPlaying = false;
    this.playbackCurrentSeconds = 0;
    if (audioEl) {
      audioEl.currentTime = 0;
    }
  }

  private startRecordTimer() {
    this.recordSegmentStartMs = Date.now();
    this.clearRecordTimer();
    this.recordTimerInterval = setInterval(() => this.tickRecordTimer(), 200);
  }

  private stopRecordTimer() {
    this.recordAccumulatedMs += Date.now() - this.recordSegmentStartMs;
    this.clearRecordTimer();
  }

  private clearRecordTimer() {
    if (this.recordTimerInterval) {
      clearInterval(this.recordTimerInterval);
      this.recordTimerInterval = null;
    }
  }

  private tickRecordTimer() {
    const elapsedMs = this.recordAccumulatedMs + (Date.now() - this.recordSegmentStartMs);
    this.recordElapsedSeconds = elapsedMs / 1000;
    const percent = (this.recordElapsedSeconds / RECORD_CURSOR_CAP_SECONDS) * RECORD_CURSOR_CAP_PERCENT;
    this.recordingCursorPercent = Math.min(RECORD_CURSOR_CAP_PERCENT, percent);
  }

  stopRecording() {
    if (this.record) {
      this.record.stop();

      // Aguardar um pouco para garantir que o ondataavailable seja chamado
      setTimeout(() => {
        if (this.currentStream) {
          this.currentStream.getTracks().forEach(track => {
            track.stop();
          });
        }
      }, 100);
    }
  }

  processRecording(blob) {
    if (blob.size === 0) {
      this.logDebug('processRecording:empty-blob');
      return;
    }

    this.blob = blob;

    if (this.url) {
      URL.revokeObjectURL(this.url);
    }

    this.url = URL.createObjectURL(blob);
    // Sanitiza uma única vez: repetir sanitize() diretamente no template recria o SafeUrl a cada
    // change detection e faz o Angular reatribuir audio.src continuamente, interrompendo o play().
    this.safeAudioUrl = this.sanitize(this.url);

    // Verificar se o blob tem conteúdo de áudio
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result as ArrayBuffer;
      this.logDebug('processRecording:array-buffer-size', String(arrayBuffer.byteLength));
    };
    reader.readAsArrayBuffer(blob);
  }

  errorCallback(error) {
    this.logDebug('errorCallback', error?.message || String(error));
    this.error = error;
    this.clearRecordTimer();
    this.viewState = 'idle';
  }

  enviaAudio() {
    if (!this.blob) {
      this.logDebug('enviaAudio:no-blob');
      return;
    }

    // Verificar se o blob tem conteúdo
    if (this.blob.size === 0) {
      this.logDebug('enviaAudio:empty-blob');
      return;
    }

    var blob = new Blob([this.blob], { type: "audio/ogg" });

    var result = {
      blob: blob,
      url: this.url,
    }
    this.activeModal.close(result);
  }

  close() {
    Swal.fire({
      title: this.translate.instant('DESEJA_CANCELAR_ACAO'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('SIM'),
      cancelButtonText: this.translate.instant('NAO'),
      position: 'top',
      customClass: {
        content: 'swal-only-text'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.clearRecordTimer();

        if (this.record && this.record.state && this.record.state !== 'inactive') {
          try {
            this.record.stop();
          } catch (error) {
            this.logDebug('close:stop-error', error?.message || String(error));
          }
        }

        if (this.currentStream) {
          this.currentStream.getTracks().forEach(track => track.stop());
        }

        if (this.url) {
          URL.revokeObjectURL(this.url);
        }

        this.activeModal.close();
      }
    });
  }

  private resetToIdle() {
    const audioEl = this.audioPlayerRef?.nativeElement;
    if (audioEl) {
      audioEl.pause();
    }

    if (this.url) {
      URL.revokeObjectURL(this.url);
    }

    this.record = null;
    this.url = null;
    this.safeAudioUrl = null;
    this.blob = null;
    this.error = null;
    this.isPlaying = false;
    this.playbackCurrentSeconds = 0;
    this.playbackDurationSeconds = 0;
    this.recordElapsedSeconds = 0;
    this.recordAccumulatedMs = 0;
    this.recordingCursorPercent = 0;
    this.viewState = 'idle';
  }
}
